import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Sparkles, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

export const SearchBar = ({ 
  onSearch, 
  onAISearch, 
  isLoading = false, 
  placeholder = "Search by name, location, or describe the person" 
}) => {
  const [query, setQuery] = useState('');
  const [useAI, setUseAI] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    if (useAI && onAISearch) {
      onAISearch(query);
    } else if (onSearch) {
      onSearch(query);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-3xl mx-auto"
      onSubmit={handleSubmit}
    >
      {/* Search Input with inline spinner */}
      <div className="relative">
        {isLoading ? (
          <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-trust-blue-500 animate-spin pointer-events-none z-10" />
        ) : (
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10" />
        )}
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          disabled={isLoading}
          className="h-14 md:h-16 w-full pl-12 pr-28 rounded-full border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-trust-blue-500 focus:ring-trust-blue-500/20 text-base shadow-sm placeholder:text-slate-500 dark:placeholder:text-slate-400 disabled:opacity-70"
          data-testid="search-input"
        />
        
        {/* Search Button - Inside the input on the right */}
        <Button
          type="submit"
          className="absolute right-2 top-1/2 -translate-y-1/2 h-10 md:h-12 px-5 md:px-6 bg-safety-orange-500 hover:bg-safety-orange-600 active:scale-95 text-white rounded-full font-semibold shadow-lg shadow-safety-orange-500/30 hover:shadow-safety-orange-500/40 transition-all duration-150 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading || !query.trim()}
          data-testid="search-button"
        >
          {isLoading ? 'Searching...' : 'Search'}
        </Button>
      </div>
      
      {/* AI Toggle + Loading State */}
      <div className="mt-3 px-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        {/* AI Toggle Checkbox */}
        <label 
          className="flex items-center gap-2 cursor-pointer group select-none"
          data-testid="ai-toggle"
        >
          <div className="relative">
            <input
              type="checkbox"
              checked={useAI}
              onChange={(e) => setUseAI(e.target.checked)}
              className="sr-only peer"
              disabled={isLoading}
            />
            <div className="w-5 h-5 border-2 border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 peer-checked:bg-trust-blue-500 peer-checked:border-trust-blue-500 transition-colors flex items-center justify-center peer-disabled:opacity-50">
              {useAI && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200 transition-colors flex items-center gap-1.5">
            <Sparkles className={`w-4 h-4 ${useAI ? 'text-trust-blue-500' : 'text-slate-400'}`} />
            Use AI-assisted matching
          </span>
          <span 
            className="text-xs text-slate-400 dark:text-slate-500 hidden sm:inline"
            title="Uses AI to expand results based on similarities, not exact matches"
          >
            (expands results based on similarities)
          </span>
        </label>
        
        {/* AI Helper Text - shown when enabled */}
        {useAI && !isLoading && (
          <motion.p
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-xs text-trust-blue-600/70 dark:text-trust-blue-400/70 italic hidden sm:block"
          >
            AI expands results based on similarities, not exact matches.
          </motion.p>
        )}
        
        {/* Loading Status Text */}
        {isLoading && (
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-trust-blue-600 dark:text-trust-blue-400 font-medium"
          >
            Finding matching cases...
          </motion.p>
        )}
      </div>
    </motion.form>
  );
};
